import { TableColumnType } from "antd";
import { DropdownProps } from "../components/visuals/CompassDropdown";
import { Content } from "../components/common/ContentBox";
import { TaskDataSource } from "./tableModels";
import { EngagementType } from "./stewardshipModels";

export enum TaskStatus {
  NOT_STARTED = "NOT_STARTED",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  STUCK = "STUCK",
}

export enum TaskPriority {
  HIGH = "HIGH",
  MEDIUM = "MEDIUM",
  LOW = "LOW",
}

export enum WsQParamType {
  VIEW_NOTE = "view-note",
  ADD_NOTE = "add-note",
  VIEW_TASK = "view-task",
  ADD_TASK = "add-task",
}

export enum SubjectType {
  FUNDS = "FUNDS",
  COMPANY = "COMPANY",
  NONE = "NONE",
  HOLDINGS = "HOLDINGS",
  ENGAGEMENT_RECORDS = "ENGAGEMENT_RECORDS",
  VOTING_RECORDS = "VOTING_RECORDS",
}

export enum FlagKey {
  IN_PROGRESS = "IN_PROGRESS",
  RESOLVED = "RESOLVED",
  BLOCKED = "BLOCKED",
  DEFAULT = "DEFAULT",
}

export type UpdateFlagParams = {
  subject_type: string;
  subject: {
    subject_id: string;
    status: FlagKey;
  };
};

export type TaskSubjectPreview = {
  subject_id: string;
  subject_type: SubjectType;
  label: string;
  image: string | null;
  meta_theme: EngagementType | null;
  context: string | null;
};

export type Task = {
  task_id: string;
  title: string;
  priority: TaskPriority;
  description: string;
  status: TaskStatus;
  date_created: string;
  date_completed: string;
  date_due: string;
  created_by: string;
  assigned_to: string;
  subject_type: string;
  subjects?: TaskSubjectPreview[];
  is_flag: boolean;
  is_archived: boolean;
  last_modified: string;
  performed_by: string;
  notes: NoteSummary[] | null[];
};

export type CreateTaskParams = {
  title: string;
  priority: TaskPriority;
  date_due?: string; // ISO 8601 date string
  assigned_to: string;
  subject_type: SubjectType;
  subject_ids?: string[];
};

export type CreateTaskConfig = {
  title: string | null;
  priority: TaskPriority | null;
  dueDate: string | null; // ISO 8601 date string
  assignee: string | null;
  subjectType: SubjectType | null;
  subjects: string[] | null;
  key: number;
};

export type UpdateTaskConfig = {
  task_id: string;
  title: string;
  priority: TaskPriority;
  date_due?: string; // ISO 8601 date string
  assigned_to: string;
  status: TaskStatus;
};

export interface TaskColumnType extends TableColumnType<TaskDataSource> {
  hasFilters?: boolean;
}

export interface NoteDropdownProps extends DropdownProps {
  notes: NoteSummary[];
  taskId: string;
}

export type NoteSummary = {
  note_id: string;
  title: string;
};

export interface Note extends Content {
  task_id: string;
}

export interface NoteResponse extends Note {
  note_id: string;
  created_by: string;
  timestamp: string; // ISO 8601 date string
}

export type TaskTrailActionType =
  | "UPDATE_TASK"
  | "ADD_TASK"
  | "DELETE_TASK"
  | "ADD_TASK_NOTE";

export interface TaskTrail {
  log_id: string;
  task_id: string;
  action_type: TaskTrailActionType;
  action_details: string;
  performed_by: string;
  timestamp: string; // ISO 8601 date string
  details: Record<string, string>;
}

export interface TrailDetailEntry {
  label: string;
  value: string;
}

export interface TrailPathItem {
  date: string; // ISO 8601 date string
  timestamp: string; // full ISO timestamp for ordering
  status: TaskStatus;
  performedBy: string;
  actionType: TaskTrailActionType;
  isNoteEvent: boolean; // true when this trail entry represents a note action
  children: string[];
  color: string;
  detailEntries: TrailDetailEntry[];
  noteIds: string[]; // populated for note events
}

export interface TaskSubjectResponse {
  is_flag: boolean;
  subject_type: SubjectType;
  task_id: string;
  subject_id: string;
  status: TaskStatus;
}
